import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from "react";
import { message } from "antd";
import { flightOperationFlightService, flightOperationTransactionService, flightOperationEventLogService, masterDataService } from "../../../../api/enhancedApi";
import { MODAL_KEYS, DEFAULT_FILTER_STATUS, DEFAULT_TRANSACTION_FILTERS } from "../constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { VIEW_TYPE, DEFAULT_ACTIVE_VIEW } from "../constants/viewConstants";

dayjs.extend(utc);

// 获取默认日期范围：D-1到D+6，共8天（UTC格式）
const getDefaultDateRange = () => {
    const today = dayjs.utc();
    const startDate = today.subtract(1, "day"); // D-1
    const endDate = today.add(6, "day"); // D+6
    return [startDate.format(), endDate.format()];
};

const getServerFilterSignature = (filters) => {
    return JSON.stringify(filters || {});
};

// 初始状态
const initialState = {
    flights: [],
    pagination: { current: 1, pageSize: 30, total: 0 },
    timelinePageSize: 300, // 新增时间轴视图的固定分页大小
    filters: {
        status: DEFAULT_FILTER_STATUS,
        departure_airport: "",
        arrival_airport: "",
        dateRange: getDefaultDateRange(), // 默认D-1到D+6，共8天
        registrations: [],
        searchText: "",
        transactionFilters: DEFAULT_TRANSACTION_FILTERS, // 新增：事务状态筛选
    },
    sorter: {},
    loading: false,
    error: null,
    selectedFlight: null,
    selectedFlightIds: [], // 用于批量操作的选中航班ID列表
    modalVisible: {
        [MODAL_KEYS.FLIGHT_DETAIL]: false,
        [MODAL_KEYS.ENHANCED_FLIGHT_TIMES]: false,
    },
    activeView: DEFAULT_ACTIVE_VIEW,
    isMobile: window.innerWidth < 768, // 添加移动设备检测
    airportsMap: {}, // 添加机场地图
    flightEvents: [], // 新增：存储航班事件日志
    loadingFlightEvents: false, // 新增：事件日志加载状态
    shouldRefreshAfterViewChange: false, // 新增：用于视图切换后刷新数据
    highlightFlightIds: [], // 数据检查"定位"目标航班ID列表
    highlightedRowId: [], // 当前表格行高亮ID列表
    deepLink: null,
};

export function parseMonitorUrlState(search) {
    const searchParams = new URLSearchParams(search || "");
    const overrides = {};

    const registrationsParam = searchParams.get("registrations");
    if (registrationsParam) {
        const registrations = registrationsParam.split(",").filter(Boolean);
        if (registrations.length > 0) overrides.registrations = registrations;
    }

    const startDate = searchParams.get("start_date");
    const endDate = searchParams.get("end_date");
    if (startDate && endDate) {
        const parsedStart = dayjs.utc(startDate);
        const parsedEnd = dayjs.utc(endDate);
        if (parsedStart.isValid() && parsedEnd.isValid()) {
            overrides.dateRange = [
                parsedStart.startOf("day").format(),
                parsedEnd.endOf("day").format(),
            ];
        }
    }

    const statusParam = searchParams.get("status");
    if (statusParam) {
        overrides.status = statusParam.split(",").filter(Boolean);
    }

    const flightIdParam = searchParams.get("flight_id");
    const flightId = flightIdParam && /^\d+$/.test(flightIdParam) ? Number.parseInt(flightIdParam, 10) : null;
    const deepLink = Number.isFinite(flightId)
        ? {
            flightId,
            action: searchParams.get("action") || null,
            detailOpened: false,
        }
        : null;

    return {
        overrides,
        deepLink,
        highlightFlightIds: deepLink ? [deepLink.flightId] : [],
    };
}

export function buildInitialMonitorStateFromUrl(init, search) {
    const { overrides, deepLink, highlightFlightIds } = parseMonitorUrlState(search);
    if (Object.keys(overrides).length === 0 && !deepLink) return init;

    return {
        ...init,
        filters: { ...init.filters, ...overrides },
        highlightFlightIds,
        deepLink,
    };
}

export function applyDeepLinkToFetchSuccess(state, flights) {
    const deepLink = state.deepLink;
    if (!deepLink || deepLink.action !== "open_detail" || deepLink.detailOpened) return {};

    const targetFlight = (flights || []).find((flight) => String(flight.id) === String(deepLink.flightId));
    if (!targetFlight) return {};

    return {
        selectedFlight: targetFlight,
        modalVisible: {
            ...state.modalVisible,
            [MODAL_KEYS.FLIGHT_DETAIL]: true,
        },
        deepLink: {
            ...deepLink,
            detailOpened: true,
        },
    };
}

// Action类型
const ActionTypes = {
    FETCH_START: "FETCH_START",
    FETCH_SUCCESS: "FETCH_SUCCESS",
    FETCH_FAILURE: "FETCH_FAILURE",
    SET_PAGINATION: "SET_PAGINATION",
    SET_FILTERS: "SET_FILTERS",
    SET_SORTER: "SET_SORTER",
    SET_SELECTED_FLIGHT: "SET_SELECTED_FLIGHT",
    SET_SELECTED_FLIGHT_IDS: "SET_SELECTED_FLIGHT_IDS", // 新增：设置选中的航班ID列表
    TOGGLE_MODAL: "TOGGLE_MODAL",
    SET_ACTIVE_VIEW: "SET_ACTIVE_VIEW",
    SET_IS_MOBILE: "SET_IS_MOBILE",
    SET_AIRPORTS_MAP: "SET_AIRPORTS_MAP",
    UPDATE_SINGLE_FLIGHT: "UPDATE_SINGLE_FLIGHT",
    UPDATE_MULTIPLE_FLIGHTS: "UPDATE_MULTIPLE_FLIGHTS",
    FETCH_EVENTS_START: "FETCH_EVENTS_START", // 新增
    FETCH_EVENTS_SUCCESS: "FETCH_EVENTS_SUCCESS", // 新增
    FETCH_EVENTS_FAILURE: "FETCH_EVENTS_FAILURE", // 新增
    CLEAR_REFRESH_FLAG: "CLEAR_REFRESH_FLAG", // 新增：用于清除刷新标志
    SET_HIGHLIGHT_FLIGHT: "SET_HIGHLIGHT_FLIGHT",
    SET_HIGHLIGHTED_ROW_ID: "SET_HIGHLIGHTED_ROW_ID",
};

// Reducer函数
function monitorReducer(state, action) {
    switch (action.type) {
        case ActionTypes.FETCH_START:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case ActionTypes.FETCH_SUCCESS:
            const successState = {
                ...state,
                loading: false,
                flights: action.payload.flights,
                pagination: {
                    ...state.pagination,
                    total: action.payload.total,
                },
            };
            return {
                ...successState,
                ...applyDeepLinkToFetchSuccess(successState, action.payload.flights),
            };

        case ActionTypes.FETCH_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload,
            };

        case ActionTypes.SET_PAGINATION:
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    ...action.payload,
                },
            };

        case ActionTypes.SET_FILTERS:
            return {
                ...state,
                filters: action.payload,
                highlightedRowId: getServerFilterSignature(state.filters) !== getServerFilterSignature(action.payload) ? [] : state.highlightedRowId,
                pagination: action.resetPagination === false
                    ? state.pagination
                    : {
                        ...state.pagination,
                        current: 1,
                    },
            };

        case ActionTypes.SET_SORTER:
            return {
                ...state,
                sorter: action.payload,
            };

        case ActionTypes.SET_SELECTED_FLIGHT:
            return {
                ...state,
                selectedFlight: action.payload,
            };

        case ActionTypes.SET_SELECTED_FLIGHT_IDS:
            return {
                ...state,
                selectedFlightIds: action.payload,
            };

        case ActionTypes.TOGGLE_MODAL:
            return {
                ...state,
                modalVisible: {
                    ...state.modalVisible,
                    [action.payload.key]: action.payload.visible,
                },
                selectedFlight: action.payload.visible && action.payload.flight ? action.payload.flight : state.selectedFlight,
                currentTransactionType: action.payload.visible && action.payload.transactionType ? action.payload.transactionType : null,
            };

        case ActionTypes.SET_ACTIVE_VIEW:
            // 创建新状态
            const newState = {
                ...state,
                activeView: action.payload,
                highlightedRowId: action.payload !== state.activeView ? [] : state.highlightedRowId,
            };

            // 如果需要刷新数据并且是切换到时间轴或日历视图，设置一个标志
            if (action.refreshData) {
                // 我们设置一个标志，表示需要刷新数据
                // 这个标志会在 useEffect 中被检测到
                newState.shouldRefreshAfterViewChange = true;
            }

            return newState;

        case ActionTypes.SET_IS_MOBILE:
            return {
                ...state,
                isMobile: action.payload,
            };

        case ActionTypes.SET_AIRPORTS_MAP:
            return {
                ...state,
                airportsMap: action.payload,
            };

        case ActionTypes.UPDATE_SINGLE_FLIGHT:
            return {
                ...state,
                flights: state.flights.map((flight) => (flight.id === action.payload.id ? action.payload : flight)),
                selectedFlight: state.selectedFlight && state.selectedFlight.id === action.payload.id ? action.payload : state.selectedFlight,
            };

        case ActionTypes.UPDATE_MULTIPLE_FLIGHTS: {
            const updatedFlights = Array.isArray(action.payload) ? action.payload : [];
            const updatedFlightById = new Map(updatedFlights.filter((flight) => flight?.id !== undefined && flight?.id !== null).map((flight) => [String(flight.id), flight]));
            if (updatedFlightById.size === 0) return state;

            return {
                ...state,
                flights: state.flights.map((flight) => updatedFlightById.get(String(flight.id)) || flight),
                selectedFlight: state.selectedFlight ? updatedFlightById.get(String(state.selectedFlight.id)) || state.selectedFlight : state.selectedFlight,
            };
        }

        case ActionTypes.FETCH_EVENTS_START: // 新增
            return {
                ...state,
                loadingFlightEvents: true,
                flightEvents: [], // 开始获取时清空旧数据
                error: null, // 清除之前的错误
            };

        case ActionTypes.FETCH_EVENTS_SUCCESS: // 新增
            return {
                ...state,
                loadingFlightEvents: false,
                flightEvents: action.payload,
            };

        case ActionTypes.FETCH_EVENTS_FAILURE: // 新增
            return {
                ...state,
                loadingFlightEvents: false,
                error: action.payload,
            };

        case ActionTypes.CLEAR_REFRESH_FLAG:
            return {
                ...state,
                shouldRefreshAfterViewChange: false,
            };

        case ActionTypes.SET_HIGHLIGHT_FLIGHT:
            return {
                ...state,
                highlightFlightIds: Array.isArray(action.payload)
                    ? action.payload
                    : action.payload ? [action.payload] : [],
            };

        case ActionTypes.SET_HIGHLIGHTED_ROW_ID:
            return {
                ...state,
                highlightedRowId: Array.isArray(action.payload)
                    ? action.payload
                    : action.payload ? [action.payload] : [],
            };

        default:
            return state;
    }
}

// 创建Context
const MonitorContext = createContext();

// Provider组件
export function MonitorProvider({ children }) {
    // --- 使用 lazy initialization 在 reducer 初始化时注入 URL 参数 ---
    // 这确保了首次 fetchFlights 就使用正确的筛选条件
    const [state, dispatch] = useReducer(monitorReducer, initialState, (init) => {
        return buildInitialMonitorStateFromUrl(init, window.location.search);
    });

    const prevFiltersRef = useRef(getServerFilterSignature(state.filters));
    const prevPaginationRef = useRef(JSON.stringify({ current: state.pagination.current, pageSize: state.pagination.pageSize }));
    const initialLoadRef = useRef(true);

    // 获取航班数据
    const fetchFlights = useCallback(async () => {
        if (state.loading) return;

        dispatch({ type: ActionTypes.FETCH_START });

        try {
            // 判断当前视图是否为时间轴或日历视图，如果是，则使用固定的300条分页大小
            const pageSize = state.activeView === VIEW_TYPE.TIMELINE || state.activeView === VIEW_TYPE.CALENDAR ? state.timelinePageSize : state.pagination.pageSize;
            const params = {
                page: state.pagination.current,
                page_size: pageSize,
                ...state.filters,
                ...(state.filters.status && Array.isArray(state.filters.status) && { status: state.filters.status.join(",") }),
                ...(state.filters.dateRange &&
                    state.filters.dateRange[0] && {
                        start_date: dayjs.utc(state.filters.dateRange[0]).format("YYYY-MM-DD"),
                        end_date: dayjs.utc(state.filters.dateRange[1]).format("YYYY-MM-DD"),
                    }),
                dateRange: undefined,
                ...(state.filters.registrations && Array.isArray(state.filters.registrations) && { aircraft_reg: state.filters.registrations.join(",") }),
                registrations: undefined,
                ...(state.filters.searchText && { search_text: state.filters.searchText }),
                searchText: undefined,
                ...(state.filters.transactionFilters && { transaction_filters: JSON.stringify(state.filters.transactionFilters) }),
                transactionFilters: undefined,
                ...(state.sorter.field && { sort_by: state.sorter.field, sort_order: state.sorter.order === "ascend" ? "asc" : "desc" }),
            };

            Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);

            const response = await flightOperationFlightService.getFlightOperationsWithTransactions(params);

            if (response.data && Array.isArray(response.data.items) && typeof response.data.total === "number") {
                dispatch({
                    type: ActionTypes.FETCH_SUCCESS,
                    payload: {
                        flights: response.data.items,
                        total: response.data.total,
                    },
                });
                message.success(`已加载 ${response.data.items.length} 条执行航班`);
            } else {
                console.error("API响应格式不符合预期:", response);
                throw new Error("获取航班数据失败: 响应格式错误");
            }
        } catch (error) {
            console.error("获取航班列表失败:", error);
            message.error("获取航班列表失败:" + error.message || "获取航班列表失败");
            dispatch({ type: ActionTypes.FETCH_FAILURE, payload: error.message || "获取航班列表失败" });
        }
    }, [state.pagination.current, state.pagination.pageSize, state.filters, state.sorter, state.activeView, state.timelinePageSize]);

    // 新增：获取指定航班的事件日志
    const fetchFlightEvents = useCallback(async (flightId) => {
        if (!flightId) return;
        dispatch({ type: ActionTypes.FETCH_EVENTS_START });
        try {
            // 调用 API 获取事件日志，假设 API 返回 { data: { items: [...] } } 格式
            const response = await flightOperationEventLogService.getFlightEventLogsByFlightId(flightId, { sort_by: "created_at", sort_order: "desc" }); // 默认按时间降序排列
            if (response.data && Array.isArray(response.data.items)) {
                dispatch({ type: ActionTypes.FETCH_EVENTS_SUCCESS, payload: response.data.items });
            } else {
                console.error("获取航班事件日志 API 响应格式不符合预期:", response);
                throw new Error("获取事件日志失败: 响应格式错误");
            }
        } catch (error) {
            console.error(`获取航班 ${flightId} 事件日志失败:`, error);
            message.error("获取航班事件日志失败:" + error.message || `获取航班 ${flightId} 事件日志失败`);
            dispatch({ type: ActionTypes.FETCH_EVENTS_FAILURE, payload: error.message || `获取事件日志失败` });
        }
    }, []); // 依赖项为空数组，因为 service 是稳定的

    // 更新分页
    const updatePagination = (pagination) => {
        dispatch({ type: ActionTypes.SET_PAGINATION, payload: pagination });
    };

    // 更新筛选条件
    const updateFilters = (filters) => {
        const formattedFilters = { ...filters };
        if (formattedFilters.dateRange && formattedFilters.dateRange[0]) {
            formattedFilters.dateRange = [dayjs.utc(formattedFilters.dateRange[0]).toISOString(), dayjs.utc(formattedFilters.dateRange[1]).toISOString()];
        } else {
            formattedFilters.dateRange = [null, null];
        }
        formattedFilters.status = Array.isArray(formattedFilters.status) ? formattedFilters.status : [];
        formattedFilters.registrations = Array.isArray(formattedFilters.registrations) ? formattedFilters.registrations : [];
        formattedFilters.departure_airport = formattedFilters.departure_airport || "";
        formattedFilters.arrival_airport = formattedFilters.arrival_airport || "";

        // 新增：事务筛选的格式化处理
        if (formattedFilters.transactionFilters) {
            // 确保每个事务类型的筛选都是数组
            Object.keys(formattedFilters.transactionFilters).forEach((transactionType) => {
                if (!Array.isArray(formattedFilters.transactionFilters[transactionType])) {
                    formattedFilters.transactionFilters[transactionType] = [];
                }
            });
        } else {
            formattedFilters.transactionFilters = DEFAULT_TRANSACTION_FILTERS;
        }

        dispatch({
            type: ActionTypes.SET_FILTERS,
            payload: formattedFilters,
            resetPagination: getServerFilterSignature(state.filters) !== getServerFilterSignature(formattedFilters),
        });
    };

    // 更新排序条件
    const updateSorter = (sorter) => {
        dispatch({ type: ActionTypes.SET_SORTER, payload: sorter });
    };

    // 切换模态框可见性
    const toggleModal = (key, visible, flight = null, transactionType = null) => {
        dispatch({ type: ActionTypes.TOGGLE_MODAL, payload: { key, visible, flight, transactionType } });
    };

    // 设置选中的航班ID列表
    const setSelectedFlightIds = (ids) => {
        dispatch({ type: ActionTypes.SET_SELECTED_FLIGHT_IDS, payload: ids });
    };

    // 添加机场数据获取逻辑
    const fetchAirportsData = useCallback(async () => {
        try {
            if (Object.keys(state.airportsMap).length > 0) return;
            if (window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
                const airportsObj = {};
                window.cachedAirports.forEach((airport) => {
                    airportsObj[airport.iata_code] = airport;
                });
                dispatch({ type: ActionTypes.SET_AIRPORTS_MAP, payload: airportsObj });
                return;
            }

            // 明确传递分页参数以获取全量机场数据
            const response = await masterDataService.airport.getAirportList({ page: 1, page_size: 500 });
            const airportList = response?.data?.items || [];
            if (airportList.length > 0) {
                window.cachedAirports = airportList;
                const airportsObj = {};
                airportList.forEach((airport) => {
                    airportsObj[airport.iata_code] = airport;
                });
                dispatch({ type: ActionTypes.SET_AIRPORTS_MAP, payload: airportsObj });
            }
        } catch (error) {
            console.error("获取机场数据失败:", error);
            message.error("获取机场数据失败:" + (error.response?.data?.detail || error.response?.data?.message || error.message || "获取机场基础数据失败，请检查网络连接或联系支持"));
            // 更新错误状态，让UI能够相应地处理
            dispatch({ type: ActionTypes.FETCH_FAILURE, payload: error.message || "获取机场数据失败" });
        }
    }, [state.airportsMap]);

    // 组件加载时获取机场数据
    useEffect(() => {
        fetchAirportsData();
    }, [fetchAirportsData]);

    // 只在filters或pagination的current/pageSize真正变化时获取航班数据
    useEffect(() => {
        const currentFilters = getServerFilterSignature(state.filters);
        const currentPageSize = JSON.stringify({ current: state.pagination.current, pageSize: state.pagination.pageSize });
        if (initialLoadRef.current || currentFilters !== prevFiltersRef.current || currentPageSize !== prevPaginationRef.current) {
            fetchFlights();
            prevFiltersRef.current = currentFilters;
            prevPaginationRef.current = currentPageSize;
            if (initialLoadRef.current) {
                initialLoadRef.current = false;
            }
        }
    }, [state.filters, state.pagination.current, state.pagination.pageSize, state.sorter, fetchFlights]); // 添加 fetchFlights 到依赖数组

    // 添加视图响应式处理
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < 768;
            if (isMobile !== state.isMobile) {
                dispatch({ type: ActionTypes.SET_IS_MOBILE, payload: isMobile });
            }
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [state.isMobile]);

    // 监听视图变化，处理数据刷新
    useEffect(() => {
        // 如果需要在视图切换后刷新数据
        if (state.shouldRefreshAfterViewChange) {
            // 清除标志，避免重复刷新
            dispatch({ type: ActionTypes.CLEAR_REFRESH_FLAG });

            // 现在安全地获取数据，此时 activeView 已经更新
            fetchFlights();
        }
    }, [state.activeView, state.shouldRefreshAfterViewChange, fetchFlights]);

    // 设置活动视图
    const setActiveView = useCallback(
        (view) => {
            if (Object.values(VIEW_TYPE).includes(view)) {
                // 先保存要切换的新视图
                const newView = view;

                // 保存判断逻辑，判断是否需要获取更多数据
                const isChangingToTimelineOrCalendar = (newView === VIEW_TYPE.TIMELINE || newView === VIEW_TYPE.CALENDAR) && state.activeView !== VIEW_TYPE.TIMELINE && state.activeView !== VIEW_TYPE.CALENDAR;

                // 直接修改 dispatch 的 action 处理函数，在 reducer 中添加处理逻辑
                dispatch({
                    type: ActionTypes.SET_ACTIVE_VIEW,
                    payload: newView,
                    // 添加一个额外的标志，表示需要在 reducer 处理完后获取数据
                    refreshData: isChangingToTimelineOrCalendar,
                });

                console.log(`切换到${newView === VIEW_TYPE.TIMELINE ? "时间轴" : newView === VIEW_TYPE.CALENDAR ? "日历" : "列表"}视图 ${isChangingToTimelineOrCalendar ? "，将自动获取更多数据" : ""}`);
            } else {
                console.warn(`Invalid view type: ${view}`);
            }
        },
        [state.activeView],
    );

    // 刷新单个航班数据
    const refreshSingleFlight = useCallback(async (flightId, showToast = true) => {
        try {
            // 使用带事务信息的批量查询API来获取单个航班数据，确保包含完整的事务信息
            const response = await flightOperationFlightService.getFlightOperationsWithTransactions({
                page: 1,
                page_size: 1,
                // 通过航班ID精确查询
                ids: flightId.toString(),
            });

            if (response.data && response.data.items && response.data.items.length > 0) {
                const updatedFlight = response.data.items[0];

                // 用更新后的航班信息替换现有列表中的对应航班
                dispatch({
                    type: ActionTypes.UPDATE_SINGLE_FLIGHT,
                    payload: updatedFlight,
                });

                return updatedFlight;
            } else {
                // 如果批量查询失败，回退到单个航班查询API
                console.warn(`批量查询航班ID=${flightId}失败，回退到单个航班查询`);
                const fallbackResponse = await flightOperationFlightService.getFlightOperationById(flightId);

                if (fallbackResponse.data) {
                    const updatedFlight = fallbackResponse.data;

                    dispatch({
                        type: ActionTypes.UPDATE_SINGLE_FLIGHT,
                        payload: updatedFlight,
                    });

                    return updatedFlight;
                } else {
                    throw new Error(`未找到ID为 ${flightId} 的航班数据`);
                }
            }
        } catch (error) {
            console.error("刷新单个航班数据失败:", error);
            if (showToast) {
                message.error(`刷新航班数据失败: ${error.message || "未知错误"}`);
            }
            return null;
        }
    }, []);

    // 批量刷新指定航班数据，仅替换返回结果命中的行对象。
    const refreshFlightsByIds = useCallback(async (flightIds, showToast = true) => {
        const ids = Array.isArray(flightIds)
            ? [...new Set(flightIds.filter((flightId) => flightId !== undefined && flightId !== null && flightId !== ""))]
            : [];

        if (ids.length === 0) return [];

        try {
            const response = await flightOperationFlightService.getFlightOperationsWithTransactions({
                page: 1,
                page_size: ids.length,
                ids: ids.join(","),
            });

            if (response.data && Array.isArray(response.data.items)) {
                const updatedFlights = response.data.items;

                dispatch({
                    type: ActionTypes.UPDATE_MULTIPLE_FLIGHTS,
                    payload: updatedFlights,
                });

                return updatedFlights;
            }

            throw new Error("批量刷新航班数据失败: 响应格式错误");
        } catch (error) {
            console.error("批量刷新航班数据失败:", error);
            if (showToast) {
                message.error(`刷新航班数据失败: ${error.message || "未知错误"}`);
            }
            return [];
        }
    }, []);

    const setHighlightedRowId = useCallback((flightIds) => {
        dispatch({ type: ActionTypes.SET_HIGHLIGHTED_ROW_ID, payload: flightIds });
    }, []);

    const clearHighlight = useCallback(() => {
        setHighlightedRowId([]);
    }, [setHighlightedRowId]);

    const refreshAndHighlightFlight = useCallback(
        async (flightId, responseData) => {
            try {
                const affectedFlightIds = Array.isArray(responseData?.affected_flight_ids) && responseData.affected_flight_ids.length > 0
                    ? responseData.affected_flight_ids
                    : [flightId];
                const updatedFlights = await refreshFlightsByIds(affectedFlightIds, true);
                if (updatedFlights.length > 0 && flightId) {
                    setHighlightedRowId([flightId]);
                }
            } catch (error) {
                console.error("刷新并高亮显示航班失败:", error);
            }
        },
        [refreshFlightsByIds, setHighlightedRowId],
    );

    const setHighlightFlightIds = useCallback((flightIds) => {
        dispatch({ type: ActionTypes.SET_HIGHLIGHT_FLIGHT, payload: flightIds });
    }, []);

    // 提供给子组件的值
    const contextValue = {
        ...state,
        fetchFlights,
        updatePagination,
        updateFilters,
        updateSorter,
        toggleModal,
        setSelectedFlightIds,
        fetchAirportsData,
        setActiveView,
        refreshSingleFlight,
        refreshFlightsByIds,
        refreshAndHighlightFlight,
        fetchFlightEvents,
        setHighlightFlightIds,
        setHighlightedRowId,
        clearHighlight,
    };

    return <MonitorContext.Provider value={contextValue}>{children}</MonitorContext.Provider>;
}

// 自定义Hook，用于组件访问Context
export function useMonitor() {
    const context = useContext(MonitorContext);
    if (!context) {
        throw new Error("useMonitor must be used within a MonitorProvider");
    }
    return context;
}

export default MonitorContext;
